import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'
import SectionHeader from './SectionHeader'
import SygnetCluster from './SygnetCluster'
import { useSubscription } from '@/hooks/useSubscription'
import { useNavigate } from 'react-router-dom'

type DashboardEmptyProps = {
  name: string
  dateLabel: string
  onCreateFirst: () => void
  onTryDemo: () => void
}

const STEP_KEYS = ['configure', 'share', 'analyze'] as const

export default function DashboardEmpty({ name, dateLabel, onCreateFirst, onTryDemo }: DashboardEmptyProps) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('dashboard')
  const navigate = useNavigate()
  const { isExpired } = useSubscription()

  return (
    <Stack spacing={3.5} sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Stack spacing={0.75}>
        <Typography variant="body2" sx={{ fontSize: 12, color: c.ink56 }}>
          {dateLabel}
        </Typography>
        <Typography variant="h1" sx={{ fontSize: 32 }}>
          {t('empty.welcome', { name })}
        </Typography>
        <Typography sx={{ fontSize: 16, color: c.ink72, maxWidth: 720, lineHeight: 1.5 }}>
          {t('empty.intro')}
        </Typography>
      </Stack>

      <Paper
        sx={{
          position: 'relative',
          overflow: 'hidden',
          background: theme.affexy.gradients.ctaCard,
          color: c.white,
          borderRadius: `${theme.affexy.radii.lg}px`,
          p: 4,
        }}
      >
        <Box
          aria-hidden
          sx={{ position: 'absolute', right: -40, top: -40, width: 320, height: 320, opacity: 0.55 }}
        >
          <SygnetCluster />
        </Box>
        <Stack spacing={1.75} sx={{ position: 'relative', maxWidth: 520 }}>
          <Typography variant="overline" sx={{ fontSize: 11, letterSpacing: '0.1em', color: 'rgba(255,255,255,.6)' }}>
            {t('empty.firstStep')}
          </Typography>
          <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 32, lineHeight: 1.15 }}>
            {t('empty.heroTitle')}
          </Typography>
          <Typography sx={{ fontSize: 14.5, color: 'rgba(255,255,255,.72)', lineHeight: 1.55, maxWidth: 480 }}>
            {t('empty.heroDesc')}
          </Typography>
          <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
            <Tooltip title={isExpired ? t('trial.blockedTooltip') : ''}>
              <span>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={isExpired ? () => navigate('/plans') : onCreateFirst}
                >
                  {t('empty.createFirst')}
                </Button>
              </span>
            </Tooltip>
            <Button
              size="large"
              onClick={onTryDemo}
              sx={{ color: '#fff', '&:hover': { bgcolor: 'rgba(255,255,255,.08)' } }}
            >
              {t('empty.tryDemo')}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Stack spacing={1.75}>
        <SectionHeader title={t('empty.howItWorks')} subtitle={t('empty.howSubtitle')} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.75 }}>
          {STEP_KEYS.map((key, i) => (
            <Paper
              key={key}
              variant="outlined"
              sx={{
                p: 2.5,
                borderRadius: `${theme.affexy.radii.md}px`,
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
              }}
            >
              <Typography
                sx={{
                  fontFamily: theme.affexy.fonts.heading,
                  fontWeight: 700,
                  fontSize: 11,
                  color: c.coral,
                  letterSpacing: '0.08em',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {t('empty.step', { n: String(i + 1).padStart(2, '0') })}
              </Typography>
              <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                {t(`empty.steps.${key}.title`)}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: c.ink72, lineHeight: 1.55 }}>
                {t(`empty.steps.${key}.desc`)}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Stack>
    </Stack>
  )
}
